import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"
import toast from "react-hot-toast"

import MarkdownResultCard from "./MarkdownResultCard"
import {
  copyText,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
  trackResultCopied,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import { generateAbaAssessmentTemplate } from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "aba_assessment_template_generator"

const AbaAssessmentTemplateGenerator = requireAuth(() => {
  const [clientInfo, setClientInfo] = useState("")
  const [assessmentType, setAssessmentType] = useState("")
  const [template, setTemplate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, { assessment_type: assessmentType })
    try {
      const data = await generateAbaAssessmentTemplate(
        clientInfo,
        assessmentType
      )
      setTemplate(data.template)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate assessment template. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  const onCopy = async () => {
    await copyText(template)
    toast.success("Copied to clipboard")
    trackResultCopied(TOOL_ID)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="ABA Assessment Template Generator"
      description="Generate an insurance-ready assessment template with all the sections needed for approval."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Client information
        </Typography>
        <TextArea
          name="Client information"
          value={clientInfo}
          onChange={(e) => setClientInfo(e.target.value)}
          placeholder="Age, diagnosis, current skills, areas of concern…"
          fullWidth
        />
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Assessment type
        </Typography>
        <TextInput
          name="Assessment type"
          value={assessmentType}
          onChange={(e) => setAssessmentType(e.target.value)}
          placeholder="e.g., Initial Assessment, FBA, VB-MAPP, ABLLS-R"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Generate assessment template"
        onClick={generate}
        disabled={loading || !clientInfo || !assessmentType}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {template && (
        <MarkdownResultCard
          title="Generated ABA assessment template"
          markdown={template}
          onCopy={onCopy}
        />
      )}
    </ToolPage>
  )
})

export default AbaAssessmentTemplateGenerator
